import { useState } from 'react';
import {
  Box, Flex, Text, useToast,
} from '@chakra-ui/react';
import { Trans, useTranslation } from 'react-i18next';

import Card from '../shared/Card';
const MCP_URL = 'https://api.mikapital.app/mcp';

function CopyButton({ value, isDisabled }) {
  const toast = useToast();
  const { t } = useTranslation('settings');
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast({ description: t('mcp.copyToasts.copied'), status: 'success', duration: 1500, isClosable: true });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({ description: t('mcp.copyToasts.failed'), status: 'error', duration: 2000, isClosable: true });
    }
  };

  return (
    <Card
      as="button"
      type="button"
      px={3}
      py={1}
      fontSize="xs"
      fontWeight={500}
      borderRadius="md"
      color={isDisabled ? 'redesign.text.muted' : 'redesign.text'}
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      opacity={isDisabled ? 0.5 : 1}
      _hover={!isDisabled ? { bg: 'redesign.bg.hover' } : undefined}
      onClick={handleCopy}
      disabled={isDisabled}
    >
      {copied ? t('mcp.copied') : t('mcp.copy')}
    </Card>
  );
}

function CodeBlock({ value }) {
  return (
    <Box mt={2} position={{ base: 'static', md: 'relative' }}>
      <Box
        bg="redesign.bg.card"
        border="1px solid"
        borderColor="redesign.border"
        borderRadius="md"
        p={3}
        pr={{ base: 3, md: '80px' }}
        fontFamily="mono"
        fontSize="xs"
        color="redesign.text"
        whiteSpace="pre-wrap"
        overflowWrap="anywhere"
      >
        {value}
      </Box>
      <Flex
        position={{ base: 'static', md: 'absolute' }}
        top={{ md: 2 }}
        right={{ md: 2 }}
        mt={{ base: 2, md: 0 }}
        justify="flex-end"
      >
        <CopyButton value={value} />
      </Flex>
    </Box>
  );
}

function SectionHeading({ children }) {
  return (
    <Text
      textStyle="microLabel"
      mb={2}
      mt={6}
    >
      {children}
    </Text>
  );
}

function Row({ label, description, action }) {
  return (
    <Flex
      direction={{ base: 'column', md: 'row' }}
      align={{ base: 'stretch', md: 'center' }}
      justify="space-between"
      gap={{ base: 2, md: 4 }}
      py={4}
      borderBottom="1px solid"
      borderColor="redesign.border"
      _last={{ borderBottom: 'none' }}
    >
      <Box minW={0} flex={1}>
        <Text fontSize="sm" fontWeight={500} color="redesign.heading">{label}</Text>
        {description && (
          <Text fontSize="xs" color="redesign.text.muted" mt={0.5}>{description}</Text>
        )}
      </Box>
      {action && <Box minW={0}>{action}</Box>}
    </Flex>
  );
}

function ClientCard({ title, children }) {
  return (
    <Card
      borderRadius="xl"
      p={4}
      mb={3}
    >
      <Text fontSize="sm" fontWeight={600} color="redesign.heading" mb={2}>
        {title}
      </Text>
      {children}
    </Card>
  );
}

function InlineCode({ children }) {
  return (
    <Box
      as="code"
      fontFamily="mono"
      fontSize="xs"
      bg="redesign.bg.card"
      border="1px solid"
      borderColor="redesign.border"
      px={1.5}
      py={0.5}
      borderRadius="sm"
      wordBreak="break-all"
    >
      {children}
    </Box>
  );
}

const CLAUDE_CODE_CMD = `claude mcp add --transport http mikapital ${MCP_URL}`;

export default function MCPContent() {
  const { t } = useTranslation('settings');
  return (
    <Box maxW="720px">
      <Text fontWeight={600} fontSize="lg" color="redesign.heading" mb={1}>
        {t('mcp.title')}
      </Text>
      <Text fontSize="sm" color="redesign.text.muted" mb={6}>
        {t('mcp.subtitle')}
      </Text>

      <SectionHeading>{t('mcp.serverHeading')}</SectionHeading>
      <Card borderRadius="xl" px={4}>
        <Row
          label={t('mcp.endpointLabel')}
          description={t('mcp.endpointDescription')}
          action={
            <Flex align="center" gap={2} flexWrap="wrap" justify={{ base: 'space-between', md: 'flex-end' }}>
              <Text fontSize="sm" color="redesign.text.muted" fontFamily="mono" wordBreak="break-all" minW={0}>
                {MCP_URL}
              </Text>
              <CopyButton value={MCP_URL} />
            </Flex>
          }
        />
      </Card>

      <SectionHeading>{t('mcp.howToHeading')}</SectionHeading>

      <ClientCard title={t('mcp.claudeDesktop.title')}>
        <Box fontSize="xs" color="redesign.text.muted">
          <Text mb={1}><Trans i18nKey="mcp.claudeDesktop.step1" ns="settings" components={{ strong: <strong /> }} /></Text>
          <Text mb={1}><Trans i18nKey="mcp.claudeDesktop.step2" ns="settings" components={{ strong: <strong /> }} /></Text>
          <Flex mb={1} gap={1} wrap="wrap" align="center">
            <Text>{t('mcp.claudeDesktop.step3')}</Text>
            <InlineCode>{MCP_URL}</InlineCode>
          </Flex>
          <Text><Trans i18nKey="mcp.claudeDesktop.step4" ns="settings" components={{ strong: <strong /> }} /></Text>
        </Box>
      </ClientCard>

      <ClientCard title={t('mcp.chatgpt.title')}>
        <Box fontSize="xs" color="redesign.text.muted">
          <Text mb={1}><Trans i18nKey="mcp.chatgpt.step1" ns="settings" components={{ strong: <strong /> }} /></Text>
          <Text mb={1}><Trans i18nKey="mcp.chatgpt.step2" ns="settings" components={{ strong: <strong /> }} /></Text>
          <Text mb={1}><Trans i18nKey="mcp.chatgpt.step3" ns="settings" components={{ strong: <strong /> }} /></Text>
          <Flex mb={1} gap={1} wrap="wrap" align="center">
            <Text>{t('mcp.chatgpt.step4')}</Text>
            <InlineCode>{MCP_URL}</InlineCode>
          </Flex>
          <Text mb={1}><Trans i18nKey="mcp.chatgpt.step5" ns="settings" components={{ strong: <strong /> }} /></Text>
          <Text><Trans i18nKey="mcp.chatgpt.step6" ns="settings" components={{ strong: <strong /> }} /></Text>
        </Box>
      </ClientCard>

      <ClientCard title={t('mcp.claudeCode.title')}>
        <Text fontSize="xs" color="redesign.text.muted">
          <Trans i18nKey="mcp.claudeCode.instructions" ns="settings" components={{ strong: <strong /> }} />
        </Text>
        <CodeBlock value={CLAUDE_CODE_CMD} />
      </ClientCard>

      <SectionHeading>{t('mcp.capabilitiesHeading')}</SectionHeading>
      <Card borderRadius="xl" px={4}>
        <Row
          label={t('mcp.capabilities.toolsLabel')}
          description={t('mcp.capabilities.toolsDescription')}
        />
        <Row
          label={t('mcp.capabilities.resourcesLabel')}
          description={t('mcp.capabilities.resourcesDescription')}
        />
        <Row
          label={t('mcp.capabilities.promptsLabel')}
          description={t('mcp.capabilities.promptsDescription')}
        />
      </Card>

      <SectionHeading>{t('mcp.examplesHeading')}</SectionHeading>
      <Card borderRadius="xl" px={4}>
        <Row label={t('mcp.examples.spending')} />
        <Row label={t('mcp.examples.lastWeek')} />
        <Row label={t('mcp.examples.compare')} />
        <Row label={t('mcp.examples.createMovement')} />
      </Card>
    </Box>
  );
}
